import { NotificationInterface, NotificationsResponseInterface } from '@elearning-platform/api-types/interfaces';
import { Color, EmptyPlaceholder } from '@elearning-platform/ui';
import { InfiniteData, QueryFunctionContext, useInfiniteQuery, UseInfiniteQueryResult } from '@tanstack/react-query';
import React, { useEffect, useMemo } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import { getNotifications } from '../../api/requests';
import { QueryKey } from '../../enums';

import { Notification } from './components';
import { useNotificationsActions } from './hooks';
import { UseNotificationsActions } from './interfaces';
import { Container, Header, LoadMoreSentinel, Wrapper } from './notifications.styled';

const Notifications: React.FC = (): JSX.Element => {
  const { t }: TransProps<never> = useTranslation();
  const { ref, inView }: ReturnType<typeof useInView> = useInView();
  const { readNotification, removeNotification, isRemoving }: UseNotificationsActions = useNotificationsActions();
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  }: UseInfiniteQueryResult<InfiniteData<NotificationsResponseInterface>, Error> = useInfiniteQuery({
    queryKey: [QueryKey.Notifications],
    queryFn: ({
      pageParam,
    }: QueryFunctionContext<string[], string | undefined>): Promise<NotificationsResponseInterface> => getNotifications(pageParam),
    initialPageParam: undefined,
    getNextPageParam: (lastPage: NotificationsResponseInterface): string | undefined => lastPage.nextPage ?? undefined,
  });
  const notifications: NotificationInterface[] = useMemo((): NotificationInterface[] => data?.pages.flatMap(
    (page: NotificationsResponseInterface): NotificationInterface[] => page.data) ?? [], [data]);

  useEffect((): void => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <Container>
      <Wrapper>
        <Header>{t('competence.app.notifications.notifications')}</Header>
        {isLoading ? (
          <EmptyPlaceholder colorsData={{ bgColor: Color.White }} isLoading text={t('competence.app.notifications.loading')} />
        ) : (
          notifications.map((notification: NotificationInterface): JSX.Element => (
            <Notification
              content={notification}
              isRemoving={isRemoving(notification.id)}
              key={notification.id}
              onDelete={(): void => removeNotification(notification.id)}
              onRead={(): void => readNotification(notification.id)}
            />
          ))
        )}
        <LoadMoreSentinel ref={ref} />
      </Wrapper>
    </Container>
  );
};

export default Notifications;
